import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

function isIdMap(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Session UI state for the outcomes dashboard:
 * - `selectedLearnerIdsByClassroom` — the learner filter committed per
 *   classroom-detail view, keyed by classroom UUID so one classroom's selection
 *   never scopes another's metrics (an absent or empty entry means all learners).
 * - `exportRequest` — the raw-data export a scope view has requested. The scope
 *   view assembles it when a level is picked and the app-root export controller
 *   ({@link CsvExportView}) consumes it, clearing it on submit; a new object
 *   reference starts a new export. Routing it through the store lets the
 *   controller live at the app root, so an in-flight export keeps polling and
 *   toasting as the viewer navigates between pages.
 * - `chartTileRendered` — whether the selected metric's tile is showing a real
 *   value rather than a loading, error, empty, or withheld state. Published by
 *   the scope view for the analytics call sites outside its subtree, which send
 *   it as the `has_chart_tile_rendered` event extension.
 *
 * Only `selectedLearnerIdsByClassroom` is persisted to `sessionStorage`
 * (survives refresh, not the session) and kept out of the server-side
 * {@link useOutcomesPrefs}. `exportRequest` is deliberately left out of
 * `partialize`: persisting it would re-submit the export on every refresh, and a
 * job already in flight resumes from its own sessionStorage entry instead.
 * Since sessionStorage outlives a logout within a tab, the persisted state is
 * cleared on both logout (`account-ui-store`) and login (`finishLoginFlow`) so
 * one identity's selection never reaches the next.
 */
export const useOutcomesUIStore = create(
  persist(
    (set) => ({
      selectedLearnerIdsByClassroom: {},
      exportRequest: undefined,
      chartTileRendered: false,

      setSelectedLearnerIds: (classroomId, ids) =>
        set((state) => ({
          selectedLearnerIdsByClassroom: {
            ...state.selectedLearnerIdsByClassroom,
            [classroomId]: ids,
          },
        })),

      setExportRequest: (request) => set({ exportRequest: request }),

      setChartTileRendered: (rendered) => set({ chartTileRendered: rendered }),

      clearStore: () =>
        set({
          selectedLearnerIdsByClassroom: {},
          exportRequest: undefined,
          chartTileRendered: false,
        }),
    }),
    {
      name: "outcomes-ui-store",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedLearnerIdsByClassroom: state.selectedLearnerIdsByClassroom,
      }),
      // Readers index straight into the map, so an entry sessionStorage carries
      // in another shape rehydrates as the empty selection rather than throwing
      // on the first read.
      merge: (persisted, current) => {
        const merged = { ...current, ...persisted };
        return isIdMap(merged.selectedLearnerIdsByClassroom)
          ? merged
          : { ...merged, selectedLearnerIdsByClassroom: {} };
      },
    }
  )
);
